/**
 * Teleprompter: der Liedtext gross und ruhig, zum Singen.
 *
 * Zwei Arten weiterzukommen:
 *   - Auto-Scroll wie im Lead Sheet (gleiche weiche Mechanik).
 *   - Sprache folgen: die Spracherkennung des Browsers hoert zu, und der
 *     Text geht erst weiter, wenn das naechste Wort gesungen bzw. gesprochen
 *     wurde. Die Ansicht gleitet dann weich zur aktuellen Zeile.
 *
 * Spracherkennung ist eine Browser-Funktion (Web Speech API). Safari auf
 * dem iPad bietet sie an; als Home-Bildschirm-App und in manchen Browsern
 * fehlt sie – dann sagt die Ansicht das, statt einen toten Knopf zu zeigen.
 */
import type { View } from '../app.ts';
import { navigate, refresh } from '../app.ts';
import { activeProject } from '../db/active.ts';
import { getSong, listSongs } from '../db/repo.ts';
import { followPosition, hasOwnLyrics, lyricsOf, normalizeWord, sheetLyrics } from '../features/prompter-text.ts';
import type { LyricsSource } from '../features/prompter-text.ts';
import { setChord } from '../chordpro/render.ts';
import { createAutoScroller, MAX_SPEED, MIN_SPEED } from '../features/autoscroll.ts';
import { button, h, select } from '../ui/dom.ts';
import { toast } from '../ui/toast.ts';

/* ------------------------------------------------------ Spracherkennung */

interface RecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  onresult: ((ev: { resultIndex: number; results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }> }) => void) | null;
  onend: (() => void) | null;
  onerror: ((ev: { error: string }) => void) | null;
}

function recognitionClass(): (new () => RecognitionLike) | null {
  const w = window as unknown as Record<string, unknown>;
  return (w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null) as (new () => RecognitionLike) | null;
}

const LANG_KEY = 'leadsheet:prompter-lang';
const SIZE_KEY = 'leadsheet:prompter-size';
const SOURCE_KEY = 'leadsheet:prompter-source';

function stored(key: string, fallback: string): string {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
}

function store(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* egal */
  }
}

/* ---------------------------------------------------------------- Ansicht */

export async function renderPrompter(params: Record<string, string>): Promise<View> {
  const project = await activeProject();
  if (!project) {
    navigate('#/projects', true);
    return { title: 'Projekte', body: h('div'), drawing: null };
  }
  const songs = (await listSongs(project.id)).sort((a, b) => a.title.localeCompare(b.title, 'de'));
  const withText = songs.filter((s) => lyricsOf(s).some((l) => l.kind === 'text'));

  const requested = params.id ? await getSong(params.id) : undefined;
  const song = requested ?? withText[0] ?? songs[0];

  const picker = select(
    [
      { value: '', label: songs.length === 0 ? 'Noch keine Songs' : 'Song wählen …' },
      ...songs.map((s) => ({ value: s.id, label: (s.title || 'Ohne Titel') + (withText.includes(s) ? '' : ' (ohne Text)') })),
    ],
    song?.id ?? '',
    (value) => value && navigate(`#/prompter/${value}`, true),
  );
  picker.classList.add('prompter__picker');

  if (!song) {
    return {
      title: 'Teleprompter',
      body: h('div', { class: 'empty' }, h('p', { class: 'empty__title', text: 'Noch keine Songs' })),
      drawing: null,
    };
  }

  /*
   * Welche Fassung: der eigene Songtext oder das Lead Sheet mit Akkorden.
   * Die Wahl gilt fuer alle Songs; fehlt eine Fassung, gilt die andere.
   */
  const ownLyrics = hasOwnLyrics(song);
  const sheetHasText = sheetLyrics(song.body).some((l) => l.kind === 'text');
  let source = stored(SOURCE_KEY, 'lyrics') as LyricsSource;
  if (source === 'sheet' && !sheetHasText) source = 'lyrics';
  const withChords = source === 'sheet' && sheetHasText;
  const lines = lyricsOf(song, withChords ? 'sheet' : undefined);
  const hasText = lines.some((l) => l.kind === 'text');

  /* ---------------------------------------------------- Text aufbauen */

  const wordEls: HTMLElement[] = [];
  const words: string[] = [];
  const lineOfWord: HTMLElement[] = [];

  const content = h('div', { class: 'prompter__text' });
  for (const line of lines) {
    if (line.kind === 'gap') {
      content.append(h('div', { class: 'prompter__gap' }));
      continue;
    }
    if (line.kind === 'label') {
      content.append(h('p', { class: 'prompter__label', text: line.text }));
      continue;
    }
    const lineEl = h('p', { class: 'prompter__line' + (withChords ? ' prompter__line--chords' : '') });
    const addWords = (target: HTMLElement, text: string) => {
      text.split(/(\s+)/).forEach((raw) => {
        if (!raw) return;
        if (/^\s+$/.test(raw)) {
          target.append(' ');
          return;
        }
        const el = h('span', { class: 'prompter__word', text: raw });
        const norm = normalizeWord(raw);
        if (norm) {
          wordEls.push(el);
          words.push(norm);
          lineOfWord.push(lineEl);
        }
        target.append(el);
      });
    };
    if (withChords && line.pairs) {
      // Akkord ueber dem Text, an der Stelle, wo er klingt.
      for (const pair of line.pairs) {
        const chord = h('span', { class: 'prompter__chord' });
        if (pair.chord) setChord(chord, pair.chord);
        else chord.innerHTML = '&nbsp;';
        const text = h('span', { class: 'prompter__pairtext' });
        addWords(text, pair.text || '\u00a0');
        lineEl.append(h('span', { class: 'prompter__pair' }, chord, text));
      }
    } else {
      addWords(lineEl, line.text);
    }
    content.append(lineEl);
  }
  content.append(h('div', { class: 'prompter__tail' }));

  const scrollEl = h(
    'div',
    { class: 'prompter__scroll' },
    hasText
      ? content
      : h(
          'div',
          { class: 'empty' },
          h('p', { class: 'empty__title', text: 'Dieser Song hat keinen Text' }),
          h('p', { text: 'Im Song-Editor den Text unter die Akkordzeilen schreiben – dann erscheint er hier.' }),
          h('div', { class: 'btn-row', style: { justifyContent: 'center' } }, button('Song bearbeiten', () => navigate(`#/song/${song.id}`), { class: 'btn btn--primary' })),
        ),
  );

  /* ------------------------------------------------------- Schriftgroesse */

  let size = Number(stored(SIZE_KEY, '44')) || 44;
  function applySize(): void {
    content.style.fontSize = `${size}px`;
    store(SIZE_KEY, String(size));
  }
  applySize();

  /* -------------------------------------------------------- Auto-Scroll */

  const scroller = createAutoScroller(scrollEl, 30, (running) => {
    playBtn.textContent = running ? 'Pause' : 'Start';
    playBtn.classList.toggle('btn--primary', !running);
  });
  const playBtn = button('Start', () => {
    if (listening) setListening(false);
    scroller.toggle();
  }, { class: 'btn btn--primary btn--small' });
  const speed = h('input', {
    class: 'prompter__speed',
    attrs: { type: 'range', min: String(MIN_SPEED), max: String(Math.min(MAX_SPEED, 120)), step: '1', 'aria-label': 'Geschwindigkeit' },
  });
  speed.value = '30';
  speed.addEventListener('input', () => scroller.setSpeed(Number(speed.value)));

  /* ------------------------------------------------------ Sprache folgen */

  const Recognition = recognitionClass();
  let recognition: RecognitionLike | null = null;
  let listening = false;
  let pos = 0;
  let heard: string[] = [];
  let lang = stored(LANG_KEY, 'de-DE');

  const langSelect = select(
    [
      { value: 'de-DE', label: 'Deutsch' },
      { value: 'en-US', label: 'Englisch' },
    ],
    lang,
    (value) => {
      lang = value;
      store(LANG_KEY, value);
      if (listening) {
        setListening(false);
        setListening(true);
      }
    },
  );
  langSelect.classList.add('prompter__lang');

  const micBtn = button('Sprache folgen', () => setListening(!listening), { class: 'btn btn--small' });
  if (!Recognition) {
    micBtn.disabled = true;
    micBtn.title = 'Dieser Browser bietet keine Spracherkennung an (am iPad: in Safari öffnen).';
  }

  function paintWords(): void {
    wordEls.forEach((el, i) => {
      el.classList.toggle('prompter__word--done', i < pos);
      el.classList.toggle('prompter__word--next', i === pos);
    });
  }

  function onHeard(transcript: string): void {
    const tokens = transcript.split(/\s+/).map(normalizeWord).filter(Boolean);
    if (tokens.length === 0) return;
    heard = [...heard, ...tokens].slice(-6);
    const next = followPosition(words, heard, pos);
    if (next > pos) {
      pos = next;
      paintWords();
      glideTo(lineOfWord[Math.min(pos, words.length - 1)] ?? null);
    }
  }

  function setListening(on: boolean): void {
    if (on && !Recognition) return;
    listening = on;
    micBtn.textContent = on ? 'Sprache: an' : 'Sprache folgen';
    micBtn.classList.toggle('btn--primary', on);
    scrollEl.classList.toggle('prompter__scroll--follow', on);
    if (on) {
      scroller.stop();
      paintWords();
      startRecognition();
    } else {
      recognition?.stop();
      recognition = null;
      wordEls.forEach((el) => el.classList.remove('prompter__word--next'));
    }
  }

  function startRecognition(): void {
    if (!Recognition || !listening) return;
    const r = new Recognition();
    r.lang = lang;
    r.continuous = true;
    r.interimResults = true;
    let seen = '';
    r.onresult = (ev) => {
      // Nur das Neue seit dem letzten Ereignis auswerten.
      let text = '';
      for (let i = ev.resultIndex; i < ev.results.length; i++) text += ' ' + (ev.results[i]?.[0]?.transcript ?? '');
      text = text.trim();
      const fresh = text.startsWith(seen) ? text.slice(seen.length) : text;
      seen = text;
      onHeard(fresh);
    };
    r.onerror = (ev) => {
      if (ev.error === 'not-allowed' || ev.error === 'service-not-allowed') {
        toast('Kein Zugriff aufs Mikrofon – in den Safari-Einstellungen erlauben.', 4000);
        setListening(false);
      }
    };
    // Safari beendet die Erkennung nach einer Pause von selbst: weiterhoeren.
    r.onend = () => {
      if (listening && recognition === r) window.setTimeout(startRecognition, 150);
    };
    recognition = r;
    try {
      r.start();
    } catch {
      /* laeuft schon */
    }
  }

  /* ---------------------------------------------- weich zur Zeile gleiten */

  let glideFrame = 0;
  function glideTo(lineEl: HTMLElement | null): void {
    if (!lineEl) return;
    const target = Math.max(0, lineEl.offsetTop - scrollEl.clientHeight * 0.32);
    cancelAnimationFrame(glideFrame);
    let current = scrollEl.scrollTop;
    const tick = () => {
      current += (target - current) * 0.09;
      if (Math.abs(target - current) < 0.5) {
        scrollEl.scrollTop = target;
        return;
      }
      scrollEl.scrollTop = current;
      glideFrame = requestAnimationFrame(tick);
    };
    glideFrame = requestAnimationFrame(tick);
  }

  function restart(): void {
    pos = 0;
    heard = [];
    paintWords();
    scroller.stop();
    glideTo(content.firstElementChild as HTMLElement | null);
  }

  /* ---------------------------------------------------------- Leiste */

  const bar = h(
    'div',
    { class: 'prompter__bar' },
    picker,
    h('div', { class: 'prompter__group' }, playBtn, speed),
    h('div', { class: 'prompter__group' }, micBtn, langSelect),
    ownLyrics && sheetHasText
      ? h(
          'div',
          { class: 'prompter__group' },
          select(
            [
              { value: 'lyrics', label: 'Songtext' },
              { value: 'sheet', label: 'Mit Akkorden' },
            ],
            withChords ? 'sheet' : 'lyrics',
            (value) => {
              store(SOURCE_KEY, value);
              refresh();
            },
          ),
        )
      : !ownLyrics && sheetHasText
        ? h(
            'div',
            { class: 'prompter__group' },
            select(
              [
                { value: 'lyrics', label: 'Nur Text' },
                { value: 'sheet', label: 'Mit Akkorden' },
              ],
              withChords ? 'sheet' : 'lyrics',
              (value) => {
                store(SOURCE_KEY, value);
                refresh();
              },
            ),
          )
        : null,
    h(
      'div',
      { class: 'prompter__group' },
      button('A−', () => ((size = Math.max(24, size - 4)), applySize()), { class: 'btn btn--ghost btn--icon', title: 'Kleiner' }),
      button('A+', () => ((size = Math.min(96, size + 4)), applySize()), { class: 'btn btn--ghost btn--icon', title: 'Größer' }),
      button('↺', restart, { class: 'btn btn--ghost btn--icon', title: 'Von vorn' }),
    ),
  );

  return {
    title: 'Teleprompter',
    subtitle: song.title || 'Ohne Titel',
    flush: true,
    body: h('div', { class: 'prompter' }, bar, scrollEl),
    drawing: null,
    cleanup: () => {
      listening = false;
      recognition?.stop();
      scroller.destroy();
      cancelAnimationFrame(glideFrame);
    },
  };
}
